import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { toHttpParamsObject } from '@capital-access/common/utils';
import { TARGETING_API_BASE_URL } from '../constants';
import { FilterRequest, FundTarget, FundTargetingFundamentals, TableResponse, TargetIdsRequest } from '../models';
import { ExpandedFundRequest, ExpandedFundResponse } from '../models/expanded-fund.response';
import { FundamentalScore } from '../models/fundamental-score.model';
import { InstitutionTargeting } from '../models/institution-targeting.model';
import { InstitutionTargetingFundamentals } from '../models/institution-targeting-fundamentals';
import { SecurityInformationResponse } from '../models/security-information.model';
import { TargetBulkResponse } from '../models/target-bulk-resposne.model';
import { TargetsRequest } from '../models/targets-request.model';
import { TargetWithContactIdResponse } from '../models/target-with-contact-id-model';
import { TargetWithContactIdsRequest } from '../models/target-with-contact-ids-request';

@Injectable()
export class TargetingRepository {
  constructor(private readonly http: HttpClient) {}

  getTarget(id: number, securityId: number): Observable<InstitutionTargeting> {
    return this.http.get<InstitutionTargeting>(`${TARGETING_API_BASE_URL}/securities/${securityId}/institutions/${id}`);
  }

  getTargetsByIds(ids: number[], securityId: number): Observable<TargetBulkResponse[]> {
    return this.http.get<TargetBulkResponse[]>(
      `${TARGETING_API_BASE_URL}/securities/${securityId}/institutions/targeting`,
      {
        params: toHttpParamsObject({ ids: ids })
      }
    );
  }

  getTargetFundamentals(id: number, securityId: number): Observable<InstitutionTargetingFundamentals> {
    return this.http.get<InstitutionTargetingFundamentals>(
      `${TARGETING_API_BASE_URL}/securities/${securityId}/institutions/${id}/fundamentals`
    );
  }

  getFundFundamentals(id: number, securityId: number): Observable<FundTargetingFundamentals> {
    return this.http.get<FundTargetingFundamentals>(
      `${TARGETING_API_BASE_URL}/securities/${securityId}/funds/${id}/fundamentals`
    );
  }

  getTopInstitutionScores(id: number, securityId: number): Observable<FundamentalScore[]> {
    return this.http.get<FundamentalScore[]>(
      `${TARGETING_API_BASE_URL}/securities/${securityId}/institutions/${id}/top-scores`
    );
  }

  getTopFundScores(id: number, securityId: number): Observable<FundamentalScore[]> {
    return this.http.get<FundamentalScore[]>(
      `${TARGETING_API_BASE_URL}/securities/${securityId}/funds/${id}/top-scores`
    );
  }

  getFundTarget(id: number, securityId: number): Observable<FundTarget> {
    return this.http.get<FundTarget>(`${TARGETING_API_BASE_URL}/securities/${securityId}/funds/${id}`);
  }

  getExpandedFunds(request: ExpandedFundRequest): Observable<ExpandedFundResponse> {
    const { securityId, institutionId, ...queryParams } = request;
    return this.http.get<ExpandedFundResponse>(
      `${TARGETING_API_BASE_URL}/securities/${securityId}/institutions/${institutionId}/funds`,
      { params: toHttpParamsObject(queryParams) }
    );
  }

  getTargets(request: TargetsRequest): Observable<TableResponse> {
    const { securityId, ...queryParams } = request;
    return this.http.get<TableResponse>(`${TARGETING_API_BASE_URL}/securities/${securityId}/institutions`, {
      params: toHttpParamsObject(queryParams)
    });
  }

  getTargetIds(request: TargetIdsRequest): Observable<number[]> {
    const { securityId, ...queryParams } = request;
    return this.http.get<number[]>(`${TARGETING_API_BASE_URL}/securities/${securityId}/institutions/ids`, {
      params: toHttpParamsObject(queryParams)
    });
  }

  getTargetContactIds(request: TargetWithContactIdsRequest): Observable<TargetWithContactIdResponse[]> {
    const { securityId, ...queryParams } = request;
    return this.http.get<TargetWithContactIdResponse[]>(
      `${TARGETING_API_BASE_URL}/securities/${securityId}/institutions/ids-with-contacts`,
      {
        params: toHttpParamsObject(queryParams)
      }
    );
  }

  getFilterId(filterRequest: FilterRequest): Observable<string> {
    return this.http.post<string>(`${TARGETING_API_BASE_URL}/filters`, filterRequest);
  }

  getSecurityInformation(securityId: number): Observable<SecurityInformationResponse> {
    return this.http.get<SecurityInformationResponse>(`${TARGETING_API_BASE_URL}/securities/${securityId}/info`);
  }
}
